import { Box } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { CommonHeader, DataTable } from "../../../components";
import useDebounce from "../../../hooks/useDebounce";
import {
  AddEditAdminCategoryModal,
  DeleteConfirmationModal,
} from "../../../modals";

import {
  deleteCategory_V2,
  getAllCategories_V2,
} from "../../../Services_V2/Category.service";
import { BasePayload, CategoryType } from "../../../types";
import { showError } from "../../../utils/error";
import styles from "./category.module.css";
import { getColumns } from "./column";
import { mobileText } from "../../../utils/responsiveStyle";

const CategoriesPage = () => {
  // Add Edit Category Modal
  const [openCategory, setOpenCategory] = useState(false);
  const [adminCategories, setAdminCategories] = useState<CategoryType[]>([]);

  // Pagination
  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);

  // Search
  const [searchTerm, setSearchTerm] = useState("");

  // Edit
  const [editableCategory, setEditableCategory] = useState<CategoryType | null>(
    null,
  );

  // Delete
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletableCategory, setDeletableCategory] =
    useState<CategoryType | null>(null);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const getPayload = useCallback((): BasePayload => {
    return {
      search: debouncedSearchTerm,
      page: currentPage + 1,
      limit: rowsPerPage,
    };
  }, [currentPage, rowsPerPage, debouncedSearchTerm]);

  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearchTerm]);

  const fetchAdminCategories = useCallback(async () => {
    try {
      const payload = getPayload();
      const res = await getAllCategories_V2(payload);
      setAdminCategories(res?.data || []);
      setTotal(res?.total || 0);
    } catch (error) {}
  }, [getPayload]);

  useEffect(() => {
    fetchAdminCategories();
  }, [currentPage, rowsPerPage, debouncedSearchTerm]);

  const refetch = useCallback(() => {
    fetchAdminCategories();
  }, [fetchAdminCategories]);

  const handleDelete = useCallback(async () => {
    setDeleteLoading(true);
    try {
      const res = await deleteCategory_V2(deletableCategory?._id as string);
      toast.success(res?.message);
      setDeletableCategory(null);
      setOpenDelete(false);
      refetch();
    } catch (error) {
      showError(error);
    } finally {
      setDeleteLoading(false);
    }
  }, [deletableCategory, refetch]);

  const columns = getColumns({
    currentPage,
    rowsPerPage,
    data: adminCategories,
  });

  return (
    <div className={styles.container}>
      <CommonHeader
        title={`Total ${total} Categories`}
        searchTerm={searchTerm}
        onSearchChange={(e: any) => setSearchTerm(e.target.value)}
        onAdd={() => {
          setEditableCategory(null);
          setOpenCategory(true);
        }}
        addLabel="Add New Category"
      />
      <Box sx={{ height: "100vh", mt: 2 }}>
        <DataTable
          data={adminCategories}
          columns={columns}
          total={total}
          page={currentPage}
          rowsPerPage={rowsPerPage}
          showPagination
          onPageChange={(_, page) => setCurrentPage(page)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(+e.target.value);
            setCurrentPage(0);
          }}
          onEdit={(row) => {
            setEditableCategory(row);
            setOpenCategory(true);
          }}
          onDelete={(row) => {
            setDeletableCategory(row);
            setOpenDelete(true);
          }}
          tableCellSx={{
            py: 0.3,
            ...mobileText,
            lineHeight: 1.2,
          }}
        />
      </Box>

      <AddEditAdminCategoryModal
        open={openCategory}
        onClose={() => setOpenCategory(false)}
        refetch={refetch}
        editableCategory={editableCategory}
      />
      <DeleteConfirmationModal
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        onConfirm={handleDelete}
        loading={deleteLoading}
      />
    </div>
  );
};

export default CategoriesPage;
